import { useEffect, useRef, useState } from 'react';
import type { LaunchState, LoadState } from '../workflowShared';
import type { Workspace } from './helpers/runScreenTypes';

const formatWorkspaceCreatedAt = (value: string): string => {
  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export function RunNewWorkspaceScreen({
  isAccountAuthenticated,
  onRequireLogin,
  workspacesState,
  workspaces,
  currentWorkspaceId,
  onSelectWorkspace,
  newWorkspaceName,
  onNewWorkspaceNameChange,
  createWorkspaceState,
  createWorkspaceError,
  onCreateWorkspace
}: {
  isAccountAuthenticated: boolean;
  onRequireLogin: () => void;
  workspacesState: LoadState;
  workspaces: Workspace[];
  currentWorkspaceId: string;
  onSelectWorkspace: (workspaceId: string) => void;
  newWorkspaceName: string;
  onNewWorkspaceNameChange: (value: string) => void;
  createWorkspaceState: LaunchState;
  createWorkspaceError: string;
  onCreateWorkspace: () => void;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const previousCreateStateRef = useRef(createWorkspaceState);

  useEffect(() => {
    if (previousCreateStateRef.current === 'submitting' && createWorkspaceState === 'idle') {
      setIsCreating(false);
    }

    previousCreateStateRef.current = createWorkspaceState;
  }, [createWorkspaceState]);

  if (!isAccountAuthenticated) {
    return (
      <main className="run-page-panel">
        <div className="run-panel-heading">
          <h1>Workspaces</h1>
        </div>

        <section className="run-settings-list" aria-label="Login required">
          <div className="run-settings-row">
            <div>
              <strong>Login required</strong>
              <span>Login with GitHub to create workspaces and group your runs.</span>
            </div>
            <button type="button" className="primary-button run-panel-action" onClick={onRequireLogin}>
              Login with GitHub
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="run-page-panel">
      <div className="run-panel-heading">
        <h1>Workspaces</h1>
        <p>Pick a workspace to group runs under, or create a new one.</p>
      </div>

      {workspacesState === 'loading' && workspaces.length === 0 && (
        <p className="run-workspace-status-note">Loading workspaces...</p>
      )}

      {workspacesState === 'error' && (
        <p className="run-workspace-status-note">Unable to load your workspaces.</p>
      )}

      {createWorkspaceError && (
        <p className="run-workspace-create-error" role="alert">{createWorkspaceError}</p>
      )}

      <div className="run-workspace-cards" aria-label="Workspaces">
        {workspaces.map((workspace) => (
          <button
            key={workspace.id}
            type="button"
            className={`run-workspace-card${workspace.id === currentWorkspaceId ? ' active' : ''}`}
            aria-pressed={workspace.id === currentWorkspaceId}
            onClick={() => onSelectWorkspace(workspace.id)}
          >
            {workspace.id === currentWorkspaceId && (
              <span className="run-workspace-card-badge">Selected</span>
            )}
            <span className="run-workspace-card-name">{workspace.name}</span>
            <span className="run-workspace-card-meta">
              /{workspace.slug} &middot; Created {formatWorkspaceCreatedAt(workspace.createdAt)}
            </span>
          </button>
        ))}

        <form
          className={`run-workspace-card run-workspace-create-card${isCreating ? ' creating' : ''}`}
          onSubmit={(event) => {
            event.preventDefault();

            if (!isCreating) {
              setIsCreating(true);
              return;
            }

            onCreateWorkspace();
          }}
        >
          {isCreating && (
            <label className="field run-workspace-create-field">
              <span>Workspace name</span>
              <input
                type="text"
                autoFocus
                value={newWorkspaceName}
                placeholder="e.g. Acme Protocol"
                disabled={createWorkspaceState === 'submitting'}
                onChange={(event) => onNewWorkspaceNameChange(event.target.value)}
              />
            </label>
          )}
          <button
            type="submit"
            className="run-workspace-create-submit"
            disabled={createWorkspaceState === 'submitting' || (isCreating && !newWorkspaceName.trim())}
          >
            {createWorkspaceState === 'submitting' ? 'Creating...' : isCreating ? 'Create workspace' : '+ New workspace'}
          </button>
        </form>
      </div>
    </main>
  );
}
